import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCurrentUser } from '@ingenico/react-section-utils';
import { CardCustom } from '@ingenico/react-ui/card-custom';
import { rxDimensionChange } from '@ingenico/react-ui/chip-control';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { Map } from '@ingenico/react-ui/map';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import classNames from 'classnames';
import moment from 'moment';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startWith, Subscription, tap } from 'rxjs';
import { useGoogleMapConfig } from '../../hooks/useGoogleMapConfig.hook';
import { dashString } from '../../utils/dashString.util';
import { getDisplayedGeofenceData } from '../../utils/deviceAlerts.util';
import { LogicalDeviceLocationCardSubtitle } from '../logical-device-location-card-subtitle/LogicalDeviceLocationCardSubtitle.ctrl';
import { ReadonlyLabelWithIcon } from '../readonly-label-with-icon';
import type { ReadonlyLabelWithIconData } from '../readonly-label-with-icon';
import { StatusChips } from '../status-chips';
import type { LogicalDeviceLocationCardProps } from './logicalDeviceLocationCard.model';
import styles from './logicalDeviceLocationCard.module.scss';
import { getGoogleMapLocationData, getReadonlyLabelWithIconData } from './logicalDeviceLocationCard.util';

export function LogicalDeviceLocationCard({
  dateOfReport,
  ipAddress,
  location,
  customClassName,
  displayEmptyAsDash,
  hideIcon,
  extraData,
  provider = 'GPS',
  geofencing,
  accuracy,
}: LogicalDeviceLocationCardProps): ReactNode {
  const currentUser = useCurrentUser();
  const googleMapConfig = useGoogleMapConfig();
  const googleMapLocationData = getGoogleMapLocationData(location, googleMapConfig);
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  const { getUserDateTimeFormatWithTimezone } = useUserDateTimeFormat(currentUser?.dateTimeFormat);
  const formattedDateOfReport = useMemo(() => {
    const isValidDate = moment(dateOfReport).isValid();
    return isValidDate
      ? getUserDateTimeFormatWithTimezone({ timezone: currentUser?.zoneInfo, instant: dateOfReport })
      : undefined;
  }, [currentUser?.zoneInfo, dateOfReport, getUserDateTimeFormatWithTimezone]);

  const locationData = useMemo<ReadonlyLabelWithIconData[]>(
    () => [
      ...getReadonlyLabelWithIconData({
        dateOfReport: formattedDateOfReport,
        location,
        ipAddress,
        displayEmptyAsDash,
        hideIcon,
        provider,
        accuracy,
      }),
      ...(extraData ?? []),
    ],
    [formattedDateOfReport, location, ipAddress, displayEmptyAsDash, hideIcon, provider, accuracy, extraData],
  );

  const geofencingData = useMemo<ReadonlyLabelWithIconData[]>(() => getDisplayedGeofenceData(geofencing), [geofencing]);

  const infoSectionRef = useRef<HTMLDivElement>(null);
  const [infoSectionHeight, setInfoSectionHeight] = useState<number>(0);

  const computeMapHeight = useCallback((): Subscription => {
    const subscriptions = new Subscription();
    const infoSection = infoSectionRef.current;
    if (infoSection) {
      const sub = rxDimensionChange(infoSection, 'height')
        .pipe(
          startWith(void 0),
          tap(() => {
            setInfoSectionHeight(infoSection.offsetHeight);
          }),
        )
        .subscribe();
      subscriptions.add(sub);
    }
    return subscriptions;
  }, []);

  useEffect(() => {
    const subscriptions = computeMapHeight();
    return () => subscriptions.unsubscribe();
  }, [computeMapHeight]);

  return (
    <CardCustom className={classNames(styles.container, customClassName?.container)}>
      <div className={classNames(styles.locationInfo, customClassName?.locationInfo)} ref={infoSectionRef}>
        <div className={classNames(styles.locationSection, customClassName?.locationSection)}>
          <div className={styles.locationHeader}>
            <CoreTypography customClassName={customClassName?.title} variant={CoreTypographyVariant.Title02Bold}>
              {t('devicePropertiesSections.location')}
            </CoreTypography>
            <LogicalDeviceLocationCardSubtitle
              label={t('deviceProperties.positionAsOf')}
              value={formattedDateOfReport ?? dashString()}
              tooltip={t('deviceProperties.tooltip.lastPositionAsOf')}
            />
          </div>
          <ReadonlyLabelWithIcon
            componentClassName={{ labelsContainer: styles.locationDataContainer }}
            data={locationData}
          />
        </div>
        {geofencing && (
          <div className={classNames(styles.locationSection, customClassName?.locationSection)}>
            <div className={styles.geofencingHeader}>
              <div className={styles.geofencingHeader__wrapper}>
                <CoreTypography customClassName={customClassName?.title} variant={CoreTypographyVariant.Title02Bold}>
                  {t('devicePropertiesSections.geofencing')}
                </CoreTypography>
                {geofencing.sinceDate ? (
                  <LogicalDeviceLocationCardSubtitle
                    label={t('deviceProperties.sinceGeofence')}
                    value={geofencing.sinceDate}
                    tooltip={t('deviceProperties.tooltip.geofenceDate')}
                  />
                ) : undefined}
              </div>
              <div className={styles.geofencingHeader__chip}>
                <StatusChips
                  status="error"
                  label={t('deviceProperties.outsideArea')}
                  componentsClassNames={{ container: styles.geofencingHeader__chipStatus }}
                />
              </div>
            </div>
            <ReadonlyLabelWithIcon
              data={geofencingData}
              componentClassName={{ labelsContainer: styles.geofenceAuthorizedAreasContainer }}
            />
          </div>
        )}
      </div>
      {googleMapLocationData ? (
        <div
          className={classNames(styles.mapWrapper, customClassName?.mapWrapper, {
            [styles.mapWrapper__geofencingEnabled]: !!geofencing,
          })}
          style={{ '--device-location-map-height': `${infoSectionHeight}px` } as CSSProperties}
        >
          <Map
            customClassname={styles.map}
            googleMapConfig={googleMapLocationData.googleMapConfig}
            lat={googleMapLocationData.lat}
            lng={googleMapLocationData.lng}
          />
        </div>
      ) : undefined}
    </CardCustom>
  );
}
